import { useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, type AdminFixture, type FixtureFilters } from "../../lib/api";
import { trackApiError } from "../../lib/analytics/errorTracking";
import {
  Badge,
  Button,
  EmptyState,
  ErrorNote,
  Icon,
  InfoNote,
  Input,
  Modal,
  Select,
  Skeleton,
  Switch,
  useToast,
} from "../../components/ui";

const FIXTURE_STATUSES = [
  "scheduled",
  "in_play",
  "finished",
  "postponed",
  "abandoned",
  "cancelled",
] as const;

/**
 * Every imported fixture, filterable and editable. Super admin only — the whole /admin router
 * is gated server-side, so this is not merely a hidden link.
 *
 * The point of the filters is finding the one wrong row on a Sunday night, so they narrow from
 * the top down: league, then season, then status/team/date, with free text as the escape hatch.
 */
export function FixturesPanel() {
  const [filters, setFilters] = useState<FixtureFilters>({ limit: 50, offset: 0, order: "asc" });
  const [editing, setEditing] = useState<AdminFixture | null>(null);

  const { data: leagues } = useQuery({
    queryKey: ["admin", "leagues"],
    queryFn: () => api.admin.allLeagues(),
  });

  const { data: teams } = useQuery({
    queryKey: ["admin", "teams", filters.leagueId ?? null],
    queryFn: () => api.admin.teams(filters.leagueId),
    enabled: Boolean(filters.leagueId),
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["admin", "fixtures", filters],
    queryFn: () => api.admin.fixtures(filters),
    // Keeps the table on screen while a filter change loads, instead of flashing empty.
    placeholderData: keepPreviousData,
  });

  function update(patch: Partial<FixtureFilters>) {
    // Any filter change returns to the first page — otherwise you can land on an empty page 4.
    setFilters((current) => ({ ...current, ...patch, offset: 0 }));
  }

  const league = leagues?.find((l) => l.id === filters.leagueId);
  const offset = filters.offset ?? 0;
  const limit = filters.limit ?? 50;

  return (
    <div className="space-y-4">
      <div className="card space-y-4 p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Select
            label="League"
            value={filters.leagueId ?? ""}
            onChange={(e) =>
              update({
                leagueId: e.target.value || undefined,
                // Season and team belong to the old league; keeping them would return nothing.
                seasonId: undefined,
                teamId: undefined,
              })
            }
          >
            <option value="">All leagues</option>
            {leagues?.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
                {!l.isActive && " (inactive)"}
              </option>
            ))}
          </Select>

          {league && league.seasons.length > 0 && (
            <Select
              label="Season"
              value={filters.seasonId ?? ""}
              onChange={(e) => update({ seasonId: e.target.value || undefined })}
            >
              <option value="">All seasons</option>
              {league.seasons.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label} ({s.fixtureCount})
                </option>
              ))}
            </Select>
          )}

          {teams && teams.length > 0 && (
            <Select
              label="Team"
              value={filters.teamId ?? ""}
              onChange={(e) => update({ teamId: e.target.value || undefined })}
            >
              <option value="">Any team</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          )}

          <Select
            label="Status"
            value={filters.status ?? ""}
            onChange={(e) => update({ status: e.target.value || undefined })}
          >
            <option value="">Any status</option>
            {FIXTURE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replace("_", " ")}
              </option>
            ))}
          </Select>

          <Select
            label="Source"
            value={filters.source ?? ""}
            onChange={(e) =>
              update({ source: (e.target.value || undefined) as FixtureFilters["source"] })
            }
          >
            <option value="">Any source</option>
            <option value="manual">Hand-edited</option>
            <option value="provider">Untouched feed</option>
          </Select>

          <Select
            label="Order"
            value={filters.order ?? "asc"}
            onChange={(e) => update({ order: e.target.value as "asc" | "desc" })}
          >
            <option value="asc">Earliest first</option>
            <option value="desc">Latest first</option>
          </Select>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Input
            label="Search"
            placeholder="Team name…"
            icon="search"
            value={filters.search ?? ""}
            onChange={(e) => update({ search: e.target.value || undefined })}
          />
          <Input
            label="From"
            type="date"
            value={filters.from?.slice(0, 10) ?? ""}
            onChange={(e) =>
              update({ from: e.target.value ? `${e.target.value}T00:00:00.000Z` : undefined })
            }
          />
          <Input
            label="To"
            type="date"
            value={filters.to?.slice(0, 10) ?? ""}
            onChange={(e) =>
              update({ to: e.target.value ? `${e.target.value}T23:59:59.000Z` : undefined })
            }
          />
          <div className="flex items-end">
            <Button
              variant="ghost"
              block
              onClick={() => setFilters({ limit: 50, offset: 0, order: "asc" })}
            >
              Clear filters
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 px-1 sm:flex-row sm:items-baseline sm:justify-between">
        <p className="text-sm text-fg-subtle">
          {isLoading ? (
            "Loading…"
          ) : (
            <>
              <span className="tabular font-semibold text-fg">{data?.total ?? 0}</span> fixtures
              match
              {isFetching && <span className="ml-2 text-fg-faint">updating…</span>}
            </>
          )}
        </p>

        {data && data.total > limit && (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={offset === 0}
              onClick={() =>
                setFilters((f) => ({
                  ...f,
                  offset: Math.max(0, (f.offset ?? 0) - (f.limit ?? 50)),
                }))
              }
            >
              <Icon name="chevronLeft" className="size-4" /> Previous
            </Button>
            <span className="tabular text-[13px] text-fg-subtle">
              {offset + 1}–{Math.min(offset + limit, data.total)}
            </span>
            <Button
              size="sm"
              variant="secondary"
              disabled={offset + limit >= data.total}
              onClick={() =>
                setFilters((f) => ({ ...f, offset: (f.offset ?? 0) + (f.limit ?? 50) }))
              }
            >
              Next <Icon name="chevronRight" className="size-4" />
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-96" />
      ) : !data?.fixtures.length ? (
        <EmptyState
          title="No fixtures match"
          description="Loosen the filters, or run a sync if this league has never been imported."
        />
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="mobile-scroll-tabs overflow-x-auto">
            <table className="w-full min-w-[52rem] text-sm">
              <thead>
                <tr className="border-b border-line text-left">
                  <th className="label px-4 py-3 font-semibold">Kick-off</th>
                  <th className="label px-4 py-3 font-semibold">Fixture</th>
                  <th className="label px-4 py-3 text-center font-semibold">Score</th>
                  <th className="label px-4 py-3 font-semibold">Status</th>
                  <th className="label px-4 py-3 font-semibold">Source</th>
                  <th className="label px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {data.fixtures.map((fixture) => (
                  <tr key={fixture.id} className="transition-colors hover:bg-tint">
                    <td className="whitespace-nowrap px-4 py-3 text-fg-subtle">
                      <span className="tabular">{formatDateTime(fixture.kickoffAt)}</span>
                      {fixture.leagueName && (
                        <span className="block text-[12px] text-fg-faint">
                          {fixture.leagueName}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-fg">{fixture.homeTeam.name}</span>
                      <span className="mx-1.5 text-fg-faint">v</span>
                      <span className="font-medium text-fg">{fixture.awayTeam.name}</span>
                    </td>
                    <td className="tabular whitespace-nowrap px-4 py-3 text-center font-semibold text-fg">
                      {fixture.homeScore == null || fixture.awayScore == null ? (
                        <span className="text-fg-faint">—</span>
                      ) : (
                        `${fixture.homeScore} – ${fixture.awayScore}`
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={statusTone(fixture.status)}>
                        {fixture.status.replace("_", " ")}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-[13px] text-fg-subtle">{fixture.source}</span>
                        {/* Sync skips exactly these fields, which is why it's shown per row. */}
                        {fixture.manualFields.length > 0 && (
                          <Badge tone="warn" className="normal-case">
                            edited: {fixture.manualFields.join(", ")}
                          </Badge>
                        )}
                        {fixture.locked && <Badge tone="out">locked</Badge>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="secondary" onClick={() => setEditing(fixture)}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <EditFixtureModal fixture={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

function EditFixtureModal({
  fixture,
  onClose,
}: {
  fixture: AdminFixture | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [homeScore, setHomeScore] = useState("");
  const [awayScore, setAwayScore] = useState("");
  const [status, setStatus] = useState("");
  const [locked, setLocked] = useState(false);
  const [reason, setReason] = useState("");
  const [initialised, setInitialised] = useState<string | null>(null);

  // Re-seed the form when a different fixture is opened.
  if (fixture && initialised !== fixture.id) {
    setInitialised(fixture.id);
    setHomeScore(fixture.homeScore?.toString() ?? "");
    setAwayScore(fixture.awayScore?.toString() ?? "");
    setStatus(fixture.status);
    setLocked(fixture.locked);
    setReason("");
  }

  const mutation = useMutation({
    mutationFn: () =>
      api.admin.updateFixture(fixture!.id, {
        homeScore: homeScore === "" ? null : Number(homeScore),
        awayScore: awayScore === "" ? null : Number(awayScore),
        status,
        locked,
        ...(reason ? { reason } : {}),
        resettle: true,
      }),
    onSuccess: () => {
      toast.show("Fixture updated and rounds re-settled");
      queryClient.invalidateQueries({ queryKey: ["admin", "fixtures"] });
      onClose();
    },
    onError: (error) => trackApiError(error, "admin_update_fixture"),
  });

  if (!fixture) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit fixture"
      description="Saving re-settles any round containing this fixture, reviving anyone it eliminated before re-applying the result."
      footer={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button className="flex-1" loading={mutation.isPending} onClick={() => mutation.mutate()}>
            Save changes
          </Button>
        </div>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
        className="space-y-4"
      >
        <div className="surface-soft p-3.5">
          <p className="font-medium text-fg">
            {fixture.homeTeam.name} <span className="text-fg-faint">v</span>{" "}
            {fixture.awayTeam.name}
          </p>
          <p className="mt-0.5 text-[13px] text-fg-subtle">
            {formatDateTime(fixture.kickoffAt)}
            {fixture.leagueName && ` · ${fixture.leagueName}`}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label={`${fixture.homeTeam.name} score`}
            type="number"
            min={0}
            max={99}
            value={homeScore}
            onChange={(e) => setHomeScore(e.target.value)}
            placeholder="—"
          />
          <Input
            label={`${fixture.awayTeam.name} score`}
            type="number"
            min={0}
            max={99}
            value={awayScore}
            onChange={(e) => setAwayScore(e.target.value)}
            placeholder="—"
          />
        </div>

        <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
          {FIXTURE_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replace("_", " ")}
            </option>
          ))}
        </Select>

        <div className="surface-soft flex items-start justify-between gap-4 p-3.5">
          <div className="min-w-0">
            <p className="font-medium text-fg">Lock this fixture</p>
            <p className="mt-1 text-[13px] leading-relaxed text-fg-subtle">
              Freezes the whole row against every future sync. Fields you edit here are protected
              anyway — this is for when the feed keeps getting the fixture wrong.
            </p>
          </div>
          <Switch checked={locked} onChange={setLocked} label="Lock this fixture" />
        </div>

        <Input
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Feed had the score the wrong way round"
          maxLength={280}
          hint="Recorded in the audit log. Worth writing — someone may have gone out on this result."
        />

        {mutation.error && (
          <ErrorNote>
            {mutation.error instanceof ApiError
              ? mutation.error.message
              : "Couldn't save that change."}
          </ErrorNote>
        )}

        <InfoNote icon="alert">
          Anyone eliminated by the old result is revived first, then the new result is applied.
        </InfoNote>
      </form>
    </Modal>
  );
}

function statusTone(status: string): "alive" | "out" | "warn" | "info" | "neutral" {
  if (status === "finished") return "alive";
  if (status === "in_play") return "warn";
  if (status === "postponed" || status === "abandoned" || status === "cancelled") return "out";
  return "neutral";
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
